import React from "react";
import { Row, Button, Col } from "react-bootstrap";
import moment from "moment";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

//Components
import SearchFilter from "../CustomUIComponents/SearchFilter/SearchFilter";
import Pagination from "../CustomUIComponents/Pagination/Pagination";
import CheckboxComponent from "../CustomUIComponents/CheckboxComponent/CheckboxComponent";

//Constants
import {
  SEARCH_FILTER_TYPE,
  INVOICE_DATE_FORMAT,
  DOWNLOAD_FILE_TYPE,
  SEARCH_FILTER_FIELDS,
  DATE_TYPE,
  DEFAULT_DURATION,
  DEFAULT_DATE_FORMAT,
} from "../../constants/appConstants";
import {
  EXTERNAL_RECONCILIATION_TABLE_FIELDS,
  RECONCILIATION_STATUS,
  RECONCILIATION_STATUS_ALL,
  RECONCILIATION_STATUS_VALUE,
  DOWNLOAD_URL,
} from "./Constants";
import {
  DEFAULT_PAGINATION_ACTIVE_PAGE,
  DEFAULT_PAGINATION_RECORDS_COUNT,
} from "../CustomUIComponents/Pagination/PaginationConstants";

//Utility
import Utility from "../../utils/Utility";

//Assets
import CSVdownload from "../../resources/images/ic_download_csv.svg";
import PDFdownload from "../../resources/images/ic_download_pdf.svg";

//Strings
import { Strings } from "../../resources/i18n/i18n";

// Paper-card container styling (was ExternalReconciliation.scss).
const containerStyle = {
  padding: "1.25em",
  borderRadius: "0.625em",
  boxShadow: "0 1px 0.25em 0 rgba(0, 0, 0, 0.2)",
  border: "1px solid #e0e0e0",
  backgroundColor: "#ffffff",
};

class ExternalReconciliation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      filters: {
        fromDate: DEFAULT_DURATION.fromDate,
        toDate: moment(new Date()).format(DEFAULT_DATE_FORMAT),
        matricesCode: "",
        loanNumber: "",
        status: RECONCILIATION_STATUS_ALL,
      },
      activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
      recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
      selectedOptions: [],
      errorMessage: props.errorMessage,
    };
  }

  componentDidMount() {
    this.handleOnClickSearchFilter(false);
  }

  onPageChange = ({ page, pageLength }) => {
    this.setState(
      {
        activePage: page,
        recordsPerPage: pageLength,
      },
      this.handleOnClickSearchFilter
    );
  };

  submitStatus = (statusValue) => {
    if (this.state.selectedOptions.length > 0) {
      this.props.submitInputs({
        ids: this.state.selectedOptions,
        st: statusValue,
      });
      // Clear the selection once a status change has been submitted.
      this.setState({ selectedOptions: [], errorMessage: "" });
    } else {
      this.setState({
        errorMessage: Strings("pleaseSelectCheckboxesBeforeSubmit"),
      });
    }
  };

  clearSelection = () => {
    this.setState({
      selectedOptions: [],
      errorMessage: "",
    });
  };

  // "All" is a UI-only value: st is bound to an enum on the backend, so the
  // param has to be left out entirely rather than sent as the sentinel.
  selectedStatus = () => {
    const { status } = this.state.filters;
    return status && status !== RECONCILIATION_STATUS_ALL ? status : undefined;
  };

  buildSearch = () => ({
    fd: this.state.filters.fromDate
      ? Utility.convertDateIntoMiliSeconds(this.state.filters.fromDate)
      : undefined,
    td: this.state.filters.toDate
      ? Utility.convertDateIntoMiliSeconds(this.state.filters.toDate)
      : undefined,
    cd: this.state.filters.matricesCode
      ? this.state.filters.matricesCode
      : undefined,
    ln: this.state.filters.loanNumber
      ? this.state.filters.loanNumber
      : undefined,
    st: this.selectedStatus(),
  });

  handleOnClickSearchFilter = (isDefaultOffset = false) => {
    isDefaultOffset &&
      this.setState({ activePage: DEFAULT_PAGINATION_ACTIVE_PAGE });
    let requestData = {
      search: this.buildSearch(),
      offset: isDefaultOffset
        ? DEFAULT_PAGINATION_ACTIVE_PAGE - 1
        : this.state.activePage - 1,
      limit: this.state.recordsPerPage,
    };
    this.props.handleOnClickSearchFilter(requestData);
  };

  handleOnClickResetFilter = () => {
    this.setState(
      {
        filters: {
          fromDate: DEFAULT_DURATION.fromDate,
          toDate: moment(new Date()).format(DEFAULT_DATE_FORMAT),
          matricesCode: "",
          loanNumber: "",
          status: RECONCILIATION_STATUS_ALL,
        },
        activePage: DEFAULT_PAGINATION_ACTIVE_PAGE,
        recordsPerPage: DEFAULT_PAGINATION_RECORDS_COUNT,
        errorMessage: "",
        selectedOptions: [],
      },
      this.handleOnClickSearchFilter
    );
  };

  handleInputChange = (event) => {
    this.setState({
      filters: {
        ...this.state.filters,
        [event.target.name]: event.target.value,
      },
      errorMessage: "",
    });
  };

  handleDateChange = (key) => (dateMoment) => {
    let dateValue = "";
    if (key === DATE_TYPE.FROM_DATE) {
      if (dateMoment.isAfter(this.state.filters.toDate)) {
        dateValue = this.state.filters.fromDate;
      } else {
        dateMoment.set({ hour: 0, minute: 0, second: 0 });
        dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
      }
    } else if (key === DATE_TYPE.TO_DATE) {
      if (dateMoment.isBefore(this.state.filters.fromDate)) {
        dateValue = this.state.filters.toDate;
      } else {
        dateValue = dateMoment.format(DEFAULT_DATE_FORMAT);
      }
    }
    this.setState({
      filters: { ...this.state.filters, [key]: dateValue },
      errorMessage: "",
    });
  };

  renderSearchFilter = () => {
    let searchFilterList = [
      {
        type: SEARCH_FILTER_TYPE.DATE,
        handleCalenderChange: this.handleDateChange,
        fromDate: this.state.filters.fromDate,
        toDate: this.state.filters.toDate,
        fieldname: SEARCH_FILTER_FIELDS.DATE,
        column: 4,
      },
      {
        type: SEARCH_FILTER_TYPE.INPUT,
        searchPlaceHolder: Strings("MatricesID"),
        Value: this.state.filters.matricesCode,
        handleOnChange: this.handleInputChange,
        fieldname: "matricesCode",
        name: "matricesCode",
      },
      {
        type: SEARCH_FILTER_TYPE.INPUT,
        searchPlaceHolder: Strings("applicationId"),
        Value: this.state.filters.loanNumber,
        handleOnChange: this.handleInputChange,
        inputType: SEARCH_FILTER_TYPE.NUMBER,
        fieldname: "loanNumber",
        name: "loanNumber",
      },
      {
        type: SEARCH_FILTER_TYPE.SELECTION,
        handleOnChange: this.handleInputChange,
        Value: this.state.filters.status,
        fieldname: "status",
        name: "status",
        filterDropdownList: RECONCILIATION_STATUS,
      },
    ];
    return (
      <SearchFilter
        filters={searchFilterList}
        handleOnClickSearchFilter={() => this.handleOnClickSearchFilter(true)}
        handleOnClickResetFilter={this.handleOnClickResetFilter}
        paddingRight={0}
      />
    );
  };

  // A PAID reconciliation is final and cannot be reverted, so its row is locked.
  isRowPaid = (row) => row.st === RECONCILIATION_STATUS_VALUE.PAID;

  getSelectableRows = () =>
    this.props.externalReconciliationData.filter((row) => !this.isRowPaid(row));

  onChangeRowCheckbox = (row) => {
    if (this.isRowPaid(row)) {
      return;
    }
    let selectedOptions = [...this.state.selectedOptions];
    let indexOfOption = selectedOptions.indexOf(row.id);
    if (indexOfOption === -1) {
      selectedOptions.push(row.id);
    } else {
      selectedOptions.splice(indexOfOption, 1);
    }
    this.setState({ selectedOptions, errorMessage: "" });
  };

  onHeaderCheckboxValueChange = () => {
    const selectableRows = this.getSelectableRows();
    let selectedOptions =
      this.state.selectedOptions.length === selectableRows.length
        ? []
        : selectableRows.map((row) => row.id);
    this.setState({ selectedOptions, errorMessage: "" });
  };

  renderTableHeader = (column) => {
    if (
      column.key === "sel" &&
      this.props.externalReconciliationData.length > 0
    ) {
      const selectableCount = this.getSelectableRows().length;
      return (
        <CheckboxComponent
          className='justify-content-center'
          onChange={this.onHeaderCheckboxValueChange}
          checked={
            selectableCount > 0 &&
            this.state.selectedOptions.length === selectableCount
          }
          disabled={selectableCount === 0}
        />
      );
    }
    return column.name;
  };

  renderTableData = (row, key) => {
    switch (key) {
      case "sel": {
        const isPaid = this.isRowPaid(row);
        return (
          <div
            className='d-flex justify-content-center w-100'
            style={isPaid ? { opacity: 0.5 } : undefined}
          >
            <CheckboxComponent
              onChange={() => this.onChangeRowCheckbox(row)}
              checked={isPaid || this.state.selectedOptions.includes(row.id)}
              disabled={isPaid}
            />
          </div>
        );
      }
      case "amt":
        return Utility.getCurrencyRepresentationOfAmount(row[key]);
      case "od":
      case "ca":
      case "ua":
        return row[key]
          ? moment.utc(row[key]).format(INVOICE_DATE_FORMAT)
          : Strings("NA");
      case "st":
        return row[key] ? Strings(row[key]) : Strings("NA");
      default:
        return row[key] !== null && row[key] !== undefined && row[key] !== ""
          ? row[key]
          : Strings("NA");
    }
  };

  renderTable = () => {
    const StyledTableCell = styled(TableCell)(({ theme }) => ({
      [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.divider,
        color: theme.palette.common.black,
      },
      [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
      },
    }));

    const StyledTableRow = styled(TableRow)(({ theme }) => ({
      "&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
      },
      "&:last-child td, &:last-child th": {
        border: 0,
      },
    }));

    return (
      <div className='mb-3'>
        <TableContainer component={Paper}>
          <Table aria-label='customized table'>
            <TableHead>
              <TableRow>
                {EXTERNAL_RECONCILIATION_TABLE_FIELDS.map((column) => (
                  <StyledTableCell
                    key={column.key}
                    className='text-center px-2 header'
                    title={column.name}
                  >
                    <div>{this.renderTableHeader(column)}</div>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {this.props.externalReconciliationData.map((row, rowIndex) => (
                <StyledTableRow key={rowIndex + "-" + row.id} hover>
                  {EXTERNAL_RECONCILIATION_TABLE_FIELDS.map((column) => {
                    const renderValue = this.renderTableData(row, column.key);
                    return (
                      <StyledTableCell
                        key={column.key}
                        className='text-center px-2'
                        component='th'
                        scope='row'
                        title={column.key === "sel" ? "" : renderValue}
                        style={{ minWidth: column.minWidth }}
                      >
                        {renderValue}
                      </StyledTableCell>
                    );
                  })}
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    );
  };

  downloadFile = (fileType) => {
    let searchFilterObject = { search: this.buildSearch() };
    // When the selected range exceeds the configured threshold the report is emailed.
    let rangeDays = moment(this.state.filters.toDate).diff(
      moment(this.state.filters.fromDate),
      "days"
    );
    let isDownloadFile = !this.props.maxDays || rangeDays <= this.props.maxDays;
    this.props.handleDownloadFile(
      DOWNLOAD_URL + fileType,
      searchFilterObject,
      fileType,
      isDownloadFile
    );
  };

  renderDownloadButtons = () => {
    return (
      <div>
        <Button
          className='downloadButtons me-3'
          onClick={() => {
            this.downloadFile(DOWNLOAD_FILE_TYPE.PDF);
          }}
          disabled={this.props.externalReconciliationData.length === 0}
        >
          <img src={PDFdownload} alt='' />
        </Button>
        <Button
          className='downloadButtons'
          onClick={() => {
            this.downloadFile(DOWNLOAD_FILE_TYPE.CSV);
          }}
          disabled={this.props.externalReconciliationData.length === 0}
        >
          <img src={CSVdownload} alt='' />
        </Button>
      </div>
    );
  };

  render() {
    return (
      <div className='container-fluid' style={containerStyle}>
        <Row>
          <Col md={10} className='px-0'>
            {this.renderSearchFilter()}
          </Col>
          <Col
            md={2}
            className='py-2 px-0 d-flex justify-content-end pull-right'
          >
            {this.renderDownloadButtons()}
          </Col>
        </Row>
        {this.state.errorMessage === "" ? null : (
          <Row className='errors ps-0'>{this.state.errorMessage}</Row>
        )}

        <hr className='fullWidth opacity-100' />
        {this.props.externalReconciliationData.length > 0 ? (
          <React.Fragment>
            {this.renderTable()}
            <Row className='mx-0 mt-2'>
              <Col md={12} className='d-flex justify-content-end px-0'>
                <Button
                  className='buttonBorder me-2'
                  onClick={this.clearSelection}
                >
                  {Strings("Clear")}
                </Button>
                <Button
                  className='buttonBackground'
                  onClick={() =>
                    this.submitStatus(RECONCILIATION_STATUS_VALUE.PAID)
                  }
                  disabled={this.state.selectedOptions.length === 0}
                >
                  {Strings("markAsPaid")}
                </Button>
              </Col>
            </Row>
            <Pagination
              activePage={this.state.activePage}
              recordsPerPage={this.state.recordsPerPage}
              numberOfPages={this.props.numberOfPages}
              onPageChange={this.onPageChange}
              dropup={true}
            />
          </React.Fragment>
        ) : (
          <div className='noRecordsFound noBranchRecords'>
            {Strings("NoRecordsFound")}
          </div>
        )}
      </div>
    );
  }
}

export default ExternalReconciliation;
